import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { ArtifactDetail } from 'shared'
import { Card, EmptyState, MarkdownRenderer, PageHeader, SectionSpinner } from '../../components/ui'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'
import { formatDate } from '../../lib/dates'
import { useMemberWorkspaces } from '../../components/layout'
import { DownloadReportPdf } from '../../components/report/DownloadReportPdf'

export default function MemberReport() {
  const { t, i18n } = useTranslation()
  const { workspaceId, artifactId } = useParams<{ workspaceId: string; artifactId: string }>()

  const { workspaces } = useMemberWorkspaces()
  const workspaceName = workspaces.find((w) => w.id === workspaceId)?.name

  const [artifact, setArtifact] = useState<ArtifactDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!workspaceId || !artifactId) return

    api
      .get<{ data: ArtifactDetail }>(`/api/workspaces/${workspaceId}/artifacts/${artifactId}`)
      .then((response) => setArtifact(response.data))
      .catch((err) => setError(toErrorMessage(err)))
      .finally(() => setLoading(false))
  }, [workspaceId, artifactId])

  if (loading) return <SectionSpinner />

  if (!artifact) {
    return <EmptyState title={t('errors.notFound')} description={error ?? undefined} />
  }

  return (
    <div>
      <PageHeader
        backTo={{ href: `/w/${workspaceId}`, label: t('timeline.title') }}
        title={artifact.title}
        subtitle={
          artifact.published_at
            ? t('report.publishedOn', { date: formatDate(artifact.published_at, i18n.language) })
            : undefined
        }
        actions={<DownloadReportPdf artifact={artifact} workspaceName={workspaceName} />}
      />

      {artifact.summary && (
        <p className="mb-6 text-sm text-gray-600 dark:text-gray-300">{artifact.summary}</p>
      )}

      <Card className="p-6 sm:p-8">
        {artifact.version ? (
          <MarkdownRenderer content={artifact.version.content_md} />
        ) : (
          <EmptyState title={t('report.emptyTitle')} />
        )}
      </Card>
    </div>
  )
}
